// src/components/DashboardHome.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    LinearProgress,
    Breadcrumbs,
    Link,
    Stepper,
    Step,
    StepLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Card,
    CardContent,
    IconButton,
    Menu,
    MenuItem,
    Chip,
    Avatar,
    AvatarGroup,
    Tooltip,
    Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../slices/authSlice';
import axios from 'axios';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TranslateIcon from '@mui/icons-material/Translate';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import GrainIcon from '@mui/icons-material/Grain';

import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import LockIcon from '@mui/icons-material/Lock';

import Flag from 'react-world-flags';

import isoLanguages from './isoLanguages';
import ACCOUNT_PLANS from '../config/accountPlans';
import { getProjectStatus, statusColor, getProjectProgress } from '../utils/projectStatus';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: '20px',
    textAlign: 'center',
    color: theme.palette.text.primary,
    borderRadius: '16px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
}));

// Helper: map a language code to its details (name + countryCode)
const getLanguageDetails = (code) => {
    if (!code) return { name: '', countryCode: 'UN' };
    return (
        isoLanguages.find((lang) => lang.code.toLowerCase() === code.toLowerCase()) || {
            name: code,
            countryCode: 'UN',
        }
    );
};


const getInitial = (str) =>
    typeof str === 'string' && str.length > 0 ? str[0].toUpperCase() : '';

const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

// ——— End helpers ———

// Plan‐chip mapping
const PLAN_CHIP = {
    free: {
        label: 'Free',
        icon: <WorkspacePremiumIcon />,
        color: 'default',
        style: { background: '#f0f0f0', color: '#333', fontWeight: 600 },
    },
    essential: {
        label: 'Essential',
        icon: <WorkspacePremiumIcon />,
        color: 'primary',
        style: { background: '#e3f0ff', color: '#1565c0', fontWeight: 600 },
    },
    growth: {
        label: 'Growth',
        icon: <TrendingUpIcon />,
        color: 'success',
        style: { background: '#e6f8ec', color: '#1b5e20', fontWeight: 600 },
    },
    scale: {
        label: 'Scale',
        icon: <MilitaryTechIcon />,
        color: 'secondary',
        style: { background: '#f3e8fd', color: '#6a1b9a', fontWeight: 600 },
    },
};

// ——— Extracted component: RecentProjectCard ———
function RecentProjectCard({ project, token }) {
    const navigate = useNavigate();

    // Menu anchor state
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const handleOpenMenu = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const doEdit = () => {
        navigate(`/dashboard/projects/${project._id}/edit`);
        handleCloseMenu();
    };
    const progress = getProjectProgress(project);
    const isFullyTranslated = progress >= 100;

    const doDownloadOriginal = () => {
        axios
            .get(`/api/projects/${project._id}/download?type=original`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            })
            .then((response) => {
                const blob = new Blob([response.data], { type: response.data.type });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${project._id}-original.zip`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => console.error('Error downloading original:', err));
        handleCloseMenu();
    };
    const doDownloadTranslated = () => {
        if (!isFullyTranslated) {
            handleCloseMenu();
            return;
        }
        axios
            .get(`/api/projects/${project._id}/download?type=translated`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            })
            .then((response) => {
                const blob = new Blob([response.data], { type: response.data.type });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${project._id}-translated.zip`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => console.error('Error downloading translated:', err));
        handleCloseMenu();
    };
    const doExportXliff = () => {
        axios
            .get(`/api/projects/${project._id}/xliff`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            })
            .then((response) => {
                const blob = new Blob([response.data], { type: 'application/xml' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${project._id}.xliff`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => console.error('Error exporting XLIFF:', err));
        handleCloseMenu();
    };
    const doDelete = () => {
        if (!window.confirm('Are you sure you want to delete this project?')) {
            handleCloseMenu();
            return;
        }
        axios
            .delete(`/api/projects/${project._id}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                handleCloseMenu();
            })
            .catch((err) => console.error('Error deleting project:', err));
    };

    // Compute word & segment counts
    const sections = Array.isArray(project.sections) ? project.sections : [];

    // Word & segment counts
    const segmentCount = typeof project.segmentCount === 'number'
        ? project.segmentCount
        : sections.length;

    const computedWordCount = typeof project.wordCount === 'number'
        ? project.wordCount
        : sections.reduce((sum, sec) => {
            const words = sec.split(/\s+/).filter(Boolean).length;
            return sum + words;
        }, 0);

    // Status & deadline
    const status = getProjectStatus(project);
    const dueText = project.deadline
        ? `Due: ${new Date(project.deadline).toLocaleDateString()}`
        : ''; // might be empty

    return (
        <Grid item xs={12} sm={6} md={4}>
            <Card
                sx={{
                    height: '100%', // make the card fill its grid cell
                    borderRadius: 3,
                    boxShadow: 3,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: '0.2s',
                    '&:hover': { boxShadow: 8, transform: 'translateY(-2px)' },
                    mb: 3,
                }}
                onClick={() => navigate(`/dashboard/projects/${project._id}/translate`)}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%', // make CardContent fill the card
                    }}
                >
                    {/* Top section: menu, title, flags, counts, avatars, progress */}
                    <Box>
                        <IconButton
                            size="small"
                            sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}
                            onClick={handleOpenMenu}
                        >
                            <MoreVertIcon />
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={openMenu}
                            onClose={handleCloseMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MenuItem onClick={doEdit}>
                                <i className="mdi mdi-pencil me-1"></i>Edit
                            </MenuItem>
                            <MenuItem onClick={doDownloadOriginal}>
                                <i className="mdi mdi-download me-1"></i>Download Original
                            </MenuItem>
                            <MenuItem onClick={doDownloadTranslated} disabled={!isFullyTranslated}>
                                <i className="mdi mdi-download me-1"></i>Download Translated
                            </MenuItem>
                            <MenuItem onClick={doExportXliff}>
                                <i className="mdi mdi-file-export me-1"></i>Export as XLIFF
                            </MenuItem>
                            <MenuItem onClick={doDelete}>
                                <i className="mdi mdi-delete me-1"></i>Delete
                            </MenuItem>
                        </Menu>

                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <FolderIcon color="primary" sx={{ fontSize: 28 }} />
                            <Typography variant="h6" fontWeight={700}>
                                {project.projectName}
                            </Typography>
                        </Box>

                        <Box display="flex" gap={2} mt={1} mb={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Flag
                                    code={getLanguageDetails(project.sourceLanguage).countryCode}
                                    style={{ width: 22 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {getLanguageDetails(project.sourceLanguage).name}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Flag
                                    code={getLanguageDetails(project.targetLanguage).countryCode}
                                    style={{ width: 22 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {getLanguageDetails(project.targetLanguage).name}
                                </Typography>
                            </Box>
                        </Box>

                        <Box mb={2} display="flex" alignItems="center" gap={3}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <LibraryBooksIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 16 }}>
                                    Words {computedWordCount}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <GrainIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 16 }}>
                                    Segments {segmentCount}
                                </Typography>
                            </Box>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1} mt={2} mb={2}>
                            {project.createdBy && (
                                <Tooltip
                                    title={`${project.createdBy.firstName || ''} ${project.createdBy.lastName || ''
                                        }`.trim()}
                                    arrow
                                >
                                    <Avatar
                                        src={
                                            project.createdBy.avatarUrl
                                                ? `/api/users/avatars/${project.createdBy._id}/${project.createdBy.avatarUrl
                                                    .split('/')
                                                    .pop()}`
                                                : ''
                                        }
                                        alt={`${project.createdBy.firstName || ''} ${project.createdBy.lastName || ''
                                            }`.trim()}
                                        sx={{ width: 30, height: 30, border: '2px solid #fff' }}
                                    >
                                        {!project.createdBy.avatarUrl &&
                                            (getInitial(project.createdBy.firstName) +
                                                getInitial(project.createdBy.lastName))}
                                    </Avatar>
                                </Tooltip>
                            )}

                            {Array.isArray(project.assignedTo) && project.assignedTo.length > 0 && (
                                <AvatarGroup
                                    sx={{
                                        '& .MuiAvatar-root': { border: '2px solid #fff' },
                                    }}
                                    max={4}
                                >
                                    {project.assignedTo.map((translator) => (
                                        <Tooltip
                                            key={translator._id}
                                            title={`${translator.firstName || ''} ${translator.lastName || ''
                                                }`.trim()}
                                            arrow
                                        >
                                            <Avatar
                                                src={
                                                    translator.avatarUrl
                                                        ? `/api/users/avatars/${translator._id}/${translator.avatarUrl
                                                            .split('/')
                                                            .pop()}`
                                                        : ''
                                                }
                                                alt={`${translator.firstName || ''} ${translator.lastName || ''}`.trim()}
                                                sx={{ width: 30, height: 30 }}
                                            >
                                                {!translator.avatarUrl &&
                                                    (getInitial(translator.firstName) +
                                                        getInitial(translator.lastName))}
                                            </Avatar>
                                        </Tooltip>
                                    ))}
                                </AvatarGroup>
                            )}
                        </Box>

                        <Box mt={1}>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{ borderRadius: 2, height: 8 }}
                            />
                            <Box mt={0.5} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption">{progress}% Complete</Typography>
                                <Chip
                                    size="small"
                                    label={status}
                                    color={statusColor[status]}
                                    sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Bottom section – “Due: …” always rendered so space is reserved */}
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {dueText}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );
}

// ——— Main DashboardHome component ———

const DashboardHome = () => {
    const [activeProjects, setActiveProjects] = useState(0);
    const [completedProjects, setCompletedProjects] = useState(0);
    const [recentProjects, setRecentProjects] = useState([]);
    const [wordsTranslated, setWordsTranslated] = useState(0);
    const [topLanguages, setTopLanguages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [usage, setUsage] = useState({ character_count: 0, storage_used: 0 });
    const [user, setUser] = useState({});
    const [team, setTeam] = useState([]);

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth.token);

    // Normalize accountType to lowercase for reliable comparison
    const accountType = (user.accountType || 'essential').toLowerCase();
    const chipProps = PLAN_CHIP[accountType] || PLAN_CHIP.essential;
    const currentPlan = ACCOUNT_PLANS[accountType] || ACCOUNT_PLANS.essential;
    const charLimit = currentPlan.charLimit;
    const storageLimit = currentPlan.storageLimit;

    console.log('raw user.accountType:', user.accountType);
    console.log('normalized accountType:', accountType);


    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch all projects (summary only)
                const projectsResponse = await axios.get('/api/projects?summary=true', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const { createdProjects = [], sharedProjects = [] } = projectsResponse.data;
                const allProjects = [...createdProjects, ...sharedProjects];

                setActiveProjects(allProjects.filter((project) => !project.completed).length);
                setCompletedProjects(allProjects.filter((project) => project.completed).length);

                // Sort by createdAt (descending) and keep top 3
                const sortedAll = allProjects
                    .slice()
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setRecentProjects(sortedAll.slice(0, 3));

                // Fetch stats
                const statsResponse = await axios.get('/api/stats', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const { wordsTranslated, topLanguages } = statsResponse.data;
                setWordsTranslated(wordsTranslated);
                setTopLanguages(topLanguages);

                // Fetch user profile & usage
                const userResponse = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUsage({
                    character_count: userResponse.data.deeplUsage || 0,
                    storage_used: userResponse.data.storageUsed || 0,
                });
                setUser(userResponse.data);

                // Fetch team
                const teamResponse = await axios.get('/api/team', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setTeam(teamResponse.data || []);

                setLoading(false);
            } catch (err) {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load dashboard data.');
                    setLoading(false);
                }
            }
        };
        fetchData();
    }, [token, navigate]);

    // Dynamic Greeting
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    // Breadcrumbs
    const breadcrumbs = location.pathname
        .split('/')
        .filter((x) => x)
        .map((path, index, arr) => (
            <Link
                key={path}
                color={index === arr.length - 1 ? 'textPrimary' : 'inherit'}
                onClick={() => navigate(`/${arr.slice(0, index + 1).join('/')}`)}
                sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
            >
                {path || 'Home'}
            </Link>
        ));

    // Onboarding Checklist Logic
    const isProfileComplete =
        !!user.firstName && !!user.lastName && !!user.occupation && !!user.email;
    const hasCreatedProject = activeProjects + completedProjects > 0;
    const hasTeamMembers = team && team.length > 1;

    // Build steps array conditionally
    const steps = [
        {
            label: 'Finish your profile information',
            icon: <PersonIcon color={isProfileComplete ? 'success' : 'disabled'} />,
            complete: isProfileComplete,
            action: () => navigate('/dashboard/profile'),
        },
        {
            label: 'Create your first project',
            icon: <AddCircleIcon color={hasCreatedProject ? 'success' : 'disabled'} />,
            complete: hasCreatedProject,
            action: () => navigate('/dashboard/projects/new'),
        },
    ];

    // Only include “Add team members” step if not on an essential plan
    if (accountType !== 'essential') {
        steps.push({
            label: 'Add team members',
            icon: <GroupAddIcon color={hasTeamMembers ? 'success' : 'disabled'} />,
            complete: hasTeamMembers,
            action: () => navigate('/dashboard/team'),
        });
    }

    const allComplete = steps.every((s) => s.complete);

    if (loading) return <Typography variant="h5">Loading...</Typography>;
    if (error) return <Typography variant="h5" color="error">{error}</Typography>;

    return (
        <Container maxWidth="lg" sx={{ marginTop: '30px' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {getGreeting()}
                    {user.firstName ? `, ${user.firstName}` : ''}!{' '}
                    <span role="img" aria-label="wave">
                        👋
                    </span>
                </Typography>
                <Chip
                    icon={chipProps.icon}
                    label={`${chipProps.label} Plan`}
                    sx={{
                        px: 2,
                        py: 1,
                        fontSize: 16,
                        borderRadius: '18px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
                        ...chipProps.style,
                    }}
                    size="medium"
                />
            </Box>

            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link
                    color="inherit"
                    onClick={() => navigate('/dashboard')}
                    sx={{ cursor: 'pointer' }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Dashboard
                </Link>
                {breadcrumbs}
            </Breadcrumbs>

            {/* Banner Image */}
            <Box sx={{ marginBottom: '20px', width: '100%' }}>
                <img
                    src="images/workspace-default.png"
                    alt="Workspace"
                    style={{ width: '100%', height: 'auto', borderRadius: '10px' }}
                />
            </Box>

            {/* Onboarding Checklist */}
            {!allComplete && (
                <Paper sx={{ mb: 4, p: 3, borderRadius: 3, background: '#f6fafe' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Get Started Checklist
                    </Typography>
                    <Stepper activeStep={steps.filter((s) => s.complete).length} alternativeLabel>
                        {steps.map((step) => (
                            <Step key={step.label} completed={step.complete}>
                                <StepLabel>{step.label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    <List>
                        {steps.map((step) => (
                            <ListItem
                                button
                                key={step.label}
                                onClick={step.action}
                                sx={{ opacity: step.complete ? 0.5 : 1 }}
                            >
                                <ListItemIcon>{step.icon}</ListItemIcon>
                                <ListItemText primary={step.label} />
                                <Checkbox checked={step.complete} />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            <Typography variant="h4" gutterBottom textAlign="left">
                Your Translation Workspace
            </Typography>
            <Typography variant="body1" textAlign="left" gutterBottom marginBottom="15px">
                This is your hub for managing all translation projects. Track your progress,
                explore language insights, and manage your active and completed tasks.
            </Typography>

            {/* Project Overview */}
            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    <StyledPaper sx={{ background: 'linear-gradient(135deg, #d0f0fd 0%, #a0d9ff 100%)' }}>
                        <WorkOutlineIcon fontSize="large" color="primary" />
                        <Typography variant="h6" sx={{ mt: 1 }}>
                            Active Projects
                        </Typography>
                        <Typography variant="h4">{activeProjects}</Typography>
                    </StyledPaper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <StyledPaper sx={{ background: 'linear-gradient(135deg, #e0ffe1 0%, #b3ffb5 100%)' }}>
                        <CheckCircleOutlineIcon fontSize="large" color="success" />
                        <Typography variant="h6" sx={{ mt: 1 }}>
                            Completed Projects
                        </Typography>
                        <Typography variant="h4">{completedProjects}</Typography>
                    </StyledPaper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <StyledPaper sx={{ background: 'linear-gradient(135deg, #f3e1ff 0%, #dab6ff 100%)' }}>
                        <TranslateIcon fontSize="large" color="secondary" />
                        <Typography variant="h6" sx={{ mt: 1 }}>
                            Words Translated This Month
                        </Typography>
                        <Typography variant="h4">{wordsTranslated}</Typography>
                    </StyledPaper>
                </Grid>
            </Grid>

            {/* ——— Updated Recent Projects Section ——— */}
            <Box sx={{ marginTop: '40px' }}>
                <Typography variant="h5" gutterBottom>
                    Recent Projects
                </Typography>
                {recentProjects.length > 0 ? (
                    <Grid container spacing={4} alignItems="stretch">
                        {recentProjects.map((project) => (
                            <RecentProjectCard key={project._id} project={project} token={token} />
                        ))}
                    </Grid>
                ) : (
                    <Typography>No recent projects found.</Typography>
                )}
            </Box>
            {/* ——— End Recent Projects ——— */}

            {/* Top Languages Used */}
            <Box sx={{ marginTop: '40px' }}>
                <Typography variant="h5" gutterBottom>
                    Top Languages Used
                </Typography>
                <Grid container spacing={4}>
                    {topLanguages.map((language, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <StyledPaper sx={{ backgroundColor: '#f0f4ff', textAlign: 'center', p: 4 }}>
                                <Flag
                                    code={getLanguageDetails(language.name).countryCode}
                                    style={{ width: 48 }}
                                />
                                <Typography variant="h6" sx={{ mt: 2 }}>
                                    {getLanguageDetails(language.name).name}
                                </Typography>
                                <Typography variant="body2">Used {language.count} times</Typography>
                            </StyledPaper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* User Usage Allocation */}
            <Box sx={{ marginTop: '40px' }}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" gutterBottom>
                            Machine Translation Usage
                        </Typography>
                        <StyledPaper sx={{
                            position: 'relative',
                            opacity: charLimit === 0 ? 0.7 : 1,
                            background: charLimit === 0 ? '#f8f9fa' : 'inherit',
                            minHeight: '140px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            {charLimit === 0 ? (
                                <Box sx={{ py: 2 }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                        <LockIcon color="disabled" /> Machine Translation
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                        MT characters are not included in the Free plan. Upgrade to start using automated translations.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => navigate('/dashboard/pricing')}
                                        startIcon={<TrendingUpIcon />}
                                    >
                                        Upgrade Plan
                                    </Button>
                                </Box>
                            ) : (
                                <>
                                    <Typography variant="body1">
                                        {usage.character_count.toLocaleString()} characters translated out of{' '}
                                        {charLimit >= 9007199254740991 ? 'Unlimited' : charLimit.toLocaleString()} available.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mt: 2,
                                        }}
                                    >
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                charLimit >= 9007199254740991
                                                    ? 100
                                                    : Math.min((usage.character_count / charLimit) * 100, 100)
                                            }
                                            sx={{ flexGrow: 1, mr: 2, height: 8, borderRadius: 4 }}
                                        />
                                        <Typography variant="body2">
                                            {charLimit >= 9007199254740991
                                                ? 'Unlimited'
                                                : Math.min((usage.character_count / charLimit) * 100, 100).toFixed(2) +
                                                '%'}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </StyledPaper>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default DashboardHome;
